/* eslint eslint-comments/no-use: off */
import {FormControl, Link, Portal, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'

import {getCopilotExperiments, setCopilotExperiments} from '../utils/experiments'
import styles from './ExperimentsDialog.module.css'

const experimentsQuery = 'repo:github/copilot-api path:experiments.go'

export interface ExperimentsDialogProps {
  experimentsDialogRef: React.MutableRefObject<HTMLDivElement | null>
  onDismiss: () => void
}

export const ExperimentsDialog = ({onDismiss, experimentsDialogRef}: ExperimentsDialogProps): React.ReactElement => {
  const [experiments, setExperiments] = useState(() => getCopilotExperiments().join(','))
  const [statusMessage, setStatusMessage] = useState<string | undefined>(undefined)
  const statusMessageAriaHack = useRef('\u200B')
  const saveExperiments = () => {
    const experimentsArray = []
    for (const pair of experiments.split(',')) {
      if (pair.trim() === '') continue

      if (pair.split('=').length !== 2) {
        const invalidMessage = `Invalid experiment key=value pair${statusMessageAriaHack.current}`
        setStatusMessage(invalidMessage)
        statusMessageAriaHack.current += '\u200B'
        return
      } else {
        setStatusMessage('')
      }
      experimentsArray.push(pair)
    }

    setCopilotExperiments(experimentsArray)
    onDismiss()
  }

  return (
    <Portal>
      <Dialog ref={experimentsDialogRef} onClose={onDismiss} title={'Experiments'} width="xlarge">
        <Dialog.Body>
          <div className={styles.dialogContentContainer}>
            <FormControl>
              <FormControl.Label>Experiments</FormControl.Label>
              <FormControl.Caption>
                <span>
                  List experiments as comma separated <code>key=value</code> pairs. To see available experiments,{' '}
                  <Link inline target="_blank" href={`/search?q=${encodeURIComponent(experimentsQuery)}`}>
                    read this code
                  </Link>
                  .
                </span>
              </FormControl.Caption>
              <TextInput
                aria-label="Experiments"
                name="experiments"
                value={experiments}
                onChange={e => setExperiments(e.target.value)}
                onKeyDown={e => {
                  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                  if (e.key === 'Enter') {
                    saveExperiments()
                  }
                }}
                className={styles.experimentsInput}
              />
              {statusMessage && <FormControl.Validation variant="error">{statusMessage}</FormControl.Validation>}
            </FormControl>
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Buttons
            buttons={[
              {type: 'button', onClick: onDismiss, content: 'Cancel'},
              {type: 'button', onClick: saveExperiments, content: 'Save', buttonType: 'primary'},
            ]}
          />
        </Dialog.Footer>
      </Dialog>
    </Portal>
  )
}

try{ ExperimentsDialog.displayName ||= 'ExperimentsDialog' } catch {}